import {keys, isArray, isFunction, isString, merge} from 'lodash'
import {toJS} from 'mobx'
import isEmail from 'validator/lib/isEmail'
import {Compiler} from './Compiler'

let moment = require('moment')

///////////////////////////////////////////////////////////////////////////////

export type ValidatorArgs = {
  compiler: Compiler
  rules?: ValidationRules
}

export type ValidationConstraints = {
  [key: string]: ValidationConstraint
}

export type ValidationConstraint = {
  and?: ValidationConstraints
  or?: ValidationConstraints
  if?: string | boolean
  [key: string]:
    | boolean
    | string
    | ValidationConstraintOptions
    | ValidationConstraintOverride
}

export type ValidationConstraintOptions = {
  [key: string]: any
}

export type ValidationConstraintOverride = {
  code?: string
  message?: string
  options: ValidationConstraintOptions
}

export type ValidationData = any

export type ValidationContext = any

export type ValidationRuleArgs = {
  field: string
  value: any
  code?: string
  message?: string
  options: ValidationConstraintOptions
  data: ValidationData
  ctx?: ValidationContext
}

export type ValidationRule = (
  args: ValidationRuleArgs,
) =>
  | (ValidationError | ValidationConstraints)
  | (ValidationError | ValidationConstraints)[]
  | void

export type ValidationRules = {
  [key: string]: ValidationRule
}

export type ValidationError = {
  code: string
  message: string
  data?: any
}

export type ValidationErrors = {
  [key: string]: ValidationError[]
}

///////////////////////////////////////////////////////////////////////////////

// https://www.regular-expressions.info/unicode.html#category
// https://babeljs.io/docs/en/next/babel-plugin-proposal-unicode-property-regex.html

const StdRules: ValidationRules = {
  alpha: ({value, code, message}) => {
    const regex = /^[\p{L}\p{M}]+$/u

    if (value && !regex.test(value)) {
      return {
        code: code || 'field_is_not_alpha',
        message: message || 'Field must consist of letters only',
      }
    }
  },

  numeric: ({value, code, message}) => {
    const regex = /^\d+$/

    if (value && !regex.test(value)) {
      return {
        code: code || 'field_is_not_numeric',
        message: message || 'Field must consist of numbers only',
      }
    }
  },

  alpha_numeric: ({value, code, message}) => {
    const regex = /^[\p{L}\p{M}\d]+$/u

    if (value && !regex.test(value)) {
      return {
        code: code || 'field_is_not_alpha_numeric',
        message: message || 'Field must consist of letters and numbers only',
      }
    }
  },

  alpha_dash: ({value, code, message}) => {
    const regex = /^[\p{L}\p{M}_-]+$/u

    if (value && !regex.test(value)) {
      return {
        code: code || 'field_is_not_alpha_dash',
        message: message || 'Field must consist of letters and dashes only',
      }
    }
  },

  numeric_dash: ({value, code, message}) => {
    const regex = /^[\d_-]+$/

    if (value && !regex.test(value)) {
      return {
        code: code || 'field_is_not_numeric_dash',
        message: message || 'Field must consist of numbers and dashes only',
      }
    }
  },

  alpha_numeric_dash: ({value, code, message}) => {
    const regex = /^[\p{L}\p{M}\d_-]+$/u

    if (value && !regex.test(value)) {
      return {
        code: code || 'field_is_not_alpha_numeric_dash',
        message:
          message || 'Field must consist of letters, numbers and dashes only',
      }
    }
  },

  format: ({value, options, code, message}) => {
    const format = (options || '') as string
    const regex = new RegExp(format)

    if (value && !regex.test(value)) {
      return {
        code: code || 'field_has_wrong_format',
        message: message || 'Field has wrong format',
      }
    }
  },

  email: ({value, code, message}) => {
    if (value && !isEmail(value)) {
      return {
        code: code || 'field_is_not_email',
        message: message || 'Field must be a valid email',
      }
    }
  },

  date_format: (args) => {
    let {value, code, message, ctx} = args
    let {dateStoreFormat, dateDisplayFormat} = ctx.api.computedConfig
    let interpolationData = {
      display_format: ctx.api.trans('date_display_format', {
        defaultValue: dateDisplayFormat,
      }),
    }

    if (value) {
      let date = moment(value, dateStoreFormat, true)

      if (!date.isValid()) {
        return {
          code: code || 'field_has_wrong_date_format',
          message: message || 'Field has wrong format',
          data: interpolationData,
        }
      }
    }
  },

  age: (args) => {
    let {value, options, code, message, ctx} = args
    let {dateStoreFormat} = ctx.api.computedConfig
    let {min, max} = options

    if (value) {
      let date = moment(value, dateStoreFormat, true)

      if (date.isValid()) {
        if (min) {
          let minDate = moment().subtract(min, 'years')

          if (date > minDate) {
            return {
              code: code || 'field_min_age_invalid',
              message: message || `Minimum age is ${min}`,
              data: {min},
            }
          }
        }

        if (max) {
          let maxDate = moment().subtract(max, 'years')

          if (date < maxDate) {
            return {
              code: code || 'field_max_age_invalid',
              message: message || `Maximum age is ${max}`,
              data: {max},
            }
          }
        }
      }
    }
  },

  accepted: ({value, code, message}) => {
    if (['yes', 'on', '1', 1, 'true', true].indexOf(value) === -1) {
      return {
        code: code || 'field_is_not_accepted',
        message: message || 'Field must be accepted',
      }
    }
  },

  required: ({value, options, code, message}) => {
    const isBlank = [null, undefined, ''].indexOf(value) !== -1
    const isEmptyString = isString(value) && value.trim().length === 0

    if (isBlank || isEmptyString) {
      return {
        code: code || 'field_is_missing',
        message: message || 'Field must not be empty',
      }
    }
  },

  in: ({value, options, code, message}) => {
    let list = options || []

    if (list.indexOf(value) === -1) {
      return {
        code: code || 'field_is_not_allowed',
        message: message || 'Field is not allowed',
      }
    }
  },

  nin: ({value, options, code, message}) => {
    let list = options || []

    if (list.indexOf(value) !== -1) {
      return {
        code: code || 'field_is_not_allowed',
        message: message || 'Field is not allowed',
      }
    }
  },
}

///////////////////////////////////////////////////////////////////////////////

export class Validator {
  private compiler: Compiler
  private rules: ValidationRules

  constructor(args: ValidatorArgs) {
    let {compiler, rules} = args
    rules = rules || {}

    this.compiler = compiler
    this.rules = {
      ...StdRules,
      ...rules,
    }
  }

  addRule(name: string, rule: ValidationRule) {
    this.rules[name] = rule
  }

  addRules(rules: ValidationRules) {
    this.rules = {
      ...this.rules,
      ...rules,
    }
  }

  async validate<T = any>(
    data: T,
    constraints: ValidationConstraints,
    ctx?: ValidationContext,
  ): Promise<ValidationErrors> {
    let errors: ValidationErrors = {}

    let fields = keys(constraints)

    for (let field of fields) {
      let fieldValue = data[field]
      let fieldConstraints = constraints[field]

      // check condition
      if (fieldConstraints.if) {
        if (!this.compiler.checkCondition({if: fieldConstraints.if})) {
          continue
        }
      }

      let validatorNames = keys(fieldConstraints)

      for (let validatorName of validatorNames) {
        if (['and', 'or', 'if'].indexOf(validatorName) !== -1) {
          continue
        }

        let overrides = toJS(
          fieldConstraints[validatorName],
        ) as ValidationConstraintOverride

        if ((overrides as any) === true) {
          overrides = {
            code: null,
            message: null,
            options: {},
          }
        } else if ((overrides as any) === false) {
          continue
        } else {
          overrides = {
            code: null,
            message: null,
            options: overrides as ValidationConstraintOptions,
          }
        }

        let validator = this.rules[validatorName]

        if (!validator) {
          throw new Error('Unknown validator "' + validatorName + '"')
        }

        if (isString(overrides.options)) {
          overrides.options = this.compiler.compile(overrides.options)
        }

        let result = await validator({
          field,
          data,
          ctx,
          value: fieldValue,
          code: overrides.code,
          message: overrides.message,
          options: overrides.options,
        })

        if (result) {
          if (!isArray(result)) {
            result = [result]
          }

          if (isArray(result)) {
            for (let item of result) {
              let isConstraint = !item['code'] && !item['message']

              if (isConstraint) {
                let nestedErrors = await this.validate(
                  data,
                  item as ValidationConstraints,
                  ctx,
                )
                errors = merge(errors, nestedErrors)
              } else {
                if (!errors[field]) {
                  errors[field] = []
                }

                errors[field].push(item as ValidationError)
              }
            }
          }
        }
      }

      if (!errors[field] && fieldConstraints.and) {
        let nestedErrors = await this.validate(data, fieldConstraints.and, ctx)
        errors = merge(errors, nestedErrors)
      }

      if (errors[field] && fieldConstraints.or) {
        let nestedErrors = await this.validate(data, fieldConstraints.or, ctx)
        errors = merge(errors, nestedErrors)
      }
    }

    return keys(errors).length > 0 ? errors : null
  }
}

///////////////////////////////////////////////////////////////////////////////

export function createValidator(args: ValidatorArgs): Validator {
  return new Validator(args)
}
